import React, { useState } from "react";
import { StyleSheet, View, Alert, ScrollView } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { shadowStyles } from "../../../styles/shadow";
import DocumentUpload from "../../common/general/DocumentUpload/DocumentUpload";
import { StoragePathType, useGenerateUploadUrlMutation } from "../../../graphql/generated/graphql";
// Temporarily remove icons to avoid import issues
// import { Upload, Info } from "@phosphor-icons/react";

const SubmitProject = ({ project, onSubmitProject, onBackToRequirements, loading = false, submissionData = null }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedFiles, setSubmittedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // GraphQL mutation for generating upload URLs
  const [generateUploadUrl] = useGenerateUploadUrlMutation();

  const handleFileUpload = (files) => {
    setUploadedFiles(files);
    setUploadError(null); // Clear any previous errors
  };

  // Check if all files have been successfully uploaded
  const hasSuccessfullyUploadedFiles = () => {
    return (
      uploadedFiles.length > 0 &&
      uploadedFiles.every(
        (file) => file.uploadStatus === "completed" || (file.file && file.uploadStatus !== "error") // Allow files that are ready to upload
      )
    );
  };

  const handleUploadError = (error) => {
    setUploadError(error.message);
    Alert.alert("Upload Error", error.message);
  };

  const handleSubmit = async () => {
    // TODO: this might be totally broken.
    if (uploadedFiles.length === 0) {
      Alert.alert("Missing Documents", "Please upload at least one document to submit your project.");
      return;
    }

    // Check if any files have upload errors
    const failedFiles = uploadedFiles.filter((file) => file.uploadStatus === "error");
    if (failedFiles.length > 0) {
      Alert.alert("Upload Error", "Some files failed to upload. Please remove failed files and try again.");
      return;
    }

    // Check if files are still uploading
    const uploadingFiles = uploadedFiles.filter((file) => file.uploadStatus === "uploading");
    if (uploadingFiles.length > 0) {
      Alert.alert("Upload in Progress", "Please wait for all files to finish uploading before submitting.");
      return;
    }

    try {
      setIsUploading(true);

      // TODO: file - skipped for now. looks like these pages related to Projects needs fixing entirely.
      // Generate upload URLs and get fileIds for each file
      const fileInputs = [];

      for (const file of uploadedFiles) {
        if (file.file) {
          // Generate signed URL for this file
          const response = await generateUploadUrl({
            variables: {
              fileName: file.name,
              storagePath: StoragePathType.ProjectSubmissions,
              fileTypes: [
                "application/pdf",
                "application/doc",
                "application/docx",
                "application/zip",
                "image/jpg",
                "image/jpeg",
                "image/png",
              ],
            },
          });

          if (response?.data?.generateUploadUrl?.data?.[0]) {
            const { file: fileData, signedUrl } = response.data.generateUploadUrl.data[0];
            const { id: fileId } = file || {};

            // Upload file to GCS using fetch (no axios dependency)
            const uploadResponse = await fetch(signedUrl, {
              method: "PUT",
              body: file.file,
              headers: {
                "Content-Type": file.type,
              },
            });

            if (!uploadResponse.ok) {
              throw new Error(`Failed to upload ${file.name}`);
            }

            // Add to fileInputs for the mutation
            fileInputs.push({
              fileId: fileId,
              fileName: file.name,
              fileURL: signedUrl.split("?")[0], // Remove query params
              contentType: file.type,
              size: file.size,
            });
          }
        }
      }

      // Save the submitted files and mark as submitted
      setSubmittedFiles([...uploadedFiles]);
      setHasSubmitted(true);

      // Call the submission with the fileIds
      onSubmitProject({
        files: fileInputs,
        projectId: project?.id,
      });
    } catch (error) {
      console.error("Upload error:", error);
      Alert.alert("Upload Error", `Failed to upload files: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const isResubmission = submissionData?.status === "SUBMITTED" || submissionData?.status === "REJECTED";
  const isSubmitDisabled = uploadedFiles.length === 0 || loading || isUploading || !hasSuccessfullyUploadedFiles();

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        {/* Info Box */}
        <View style={[shadowStyles.smallShadow, styles.infoBox]}>
          <View style={{ marginRight: 10 }}>
            <CustomText style={styles.infoIcon} text="ℹ️" useTranslationText={false} />
          </View>
          <View style={{ flex: 1 }}>
            <CustomText
              style={styles.infoBoxText}
              size="s"
              text={
                hasSubmitted
                  ? "Project submitted successfully! Your submission has been recorded and is under review."
                  : isResubmission
                    ? "You are resubmitting your project. Please upload updated documents and provide any additional notes."
                    : "Ready to submit your project? Upload your completed documents and deliverables. This will mark your project as submitted for final review."
              }
              useTranslationText={false}
            />
          </View>
        </View>

        {/* Project Info */}
        <View style={styles.projectInfo}>
          <CustomText
            style={styles.projectTitle}
            text={project?.name || "Project Submission"}
            size="l"
            weight="bold"
            useTranslationText={false}
          />
          {project?.description && (
            <CustomText
              style={styles.projectDescription}
              text={project.description}
              size="m"
              useTranslationText={false}
            />
          )}
        </View>

        {/* File Upload Section - Hide after submission */}
        {!hasSubmitted && (
          <View style={styles.uploadSection}>
            <View style={styles.uploadHeader}>
              <CustomText style={styles.uploadIcon} text="📁" useTranslationText={false} />
              <CustomText
                style={styles.uploadTitle}
                text="Upload Project Documents"
                size="m"
                weight="bold"
                useTranslationText={false}
              />
            </View>
            <CustomText
              style={styles.uploadDescription}
              text="Upload your completed project files, documentation, reports, or any other deliverables required for submission."
              size="s"
              useTranslationText={false}
            />

            <DocumentUpload
              onFilesSelected={handleFileUpload}
              acceptedTypes={[".pdf", ".doc", ".docx", ".zip", ".jpg", ".jpeg", ".png"]}
              maxFiles={10}
              maxFileSize={50} // 50MB
              multiple={true}
              onError={handleUploadError}
              dropzoneText="Drag & drop project files or click to browse"
              dropzoneSubtext="Upload your completed project documents, reports, and deliverables"
            />
          </View>
        )}

        {/* Submitted Files Section - Show after submission */}
        {hasSubmitted && submittedFiles.length > 0 && (
          <View style={styles.submittedSection}>
            <View style={styles.submittedHeader}>
              <CustomText style={styles.checkIcon} text="✅" useTranslationText={false} />
              <CustomText
                style={styles.submittedTitle}
                text="Submitted Documents"
                size="m"
                weight="bold"
                useTranslationText={false}
              />
            </View>
            <CustomText
              style={styles.submittedDescription}
              text="The following files have been successfully submitted:"
              size="s"
              useTranslationText={false}
            />
            <View style={styles.filesList}>
              {submittedFiles.map((file, index) => (
                <View key={index} style={styles.fileItem}>
                  <CustomText style={styles.fileIcon} text="📄" useTranslationText={false} />
                  <CustomText
                    style={styles.fileName}
                    text={file.name || `Document ${index + 1}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <View style={styles.completedBadge}>
                    <CustomText style={styles.completedText} text="SUBMITTED" useTranslationText={false} />
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Previous Submissions */}
        {submissionData && (
          <View style={styles.previousSubmissions}>
            <CustomText
              style={styles.sectionTitle}
              text="Previous Submission"
              size="m"
              weight="bold"
              useTranslationText={false}
            />
            <View style={styles.submissionCard}>
              <View style={styles.submissionHeader}>
                <CustomText
                  text={`Status: ${submissionData.status}`}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                />
                {submissionData.submittedAt && (
                  <CustomText
                    text={`Submitted: ${new Date(submissionData.submittedAt).toLocaleDateString()}`}
                    size="xs"
                    useTranslationText={false}
                  />
                )}
              </View>
              {submissionData.feedback && (
                <View style={styles.feedbackSection}>
                  <CustomText text="Feedback:" size="s" weight="bold" useTranslationText={false} />
                  <CustomText
                    text={submissionData.feedback}
                    size="s"
                    style={styles.feedbackText}
                    useTranslationText={false}
                  />
                </View>
              )}
            </View>
          </View>
        )}

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          {!hasSubmitted && (
            <CustomButton
              text={
                isSubmitDisabled && uploadedFiles.length === 0
                  ? "Upload files to submit"
                  : isSubmitDisabled && (isUploading || !hasSuccessfullyUploadedFiles())
                    ? "Waiting for upload..."
                    : isResubmission
                      ? "Resubmit Project"
                      : "Submit Project"
              }
              onPress={handleSubmit}
              size="m"
              styleType={isSubmitDisabled ? "secondary" : "primary"}
              style={[styles.submitButton, isSubmitDisabled && styles.submitButtonDisabled]}
              textStyle={[styles.submitButtonText, isSubmitDisabled && styles.submitButtonTextDisabled]}
              loading={loading || isUploading}
              disabled={isSubmitDisabled}
              aria-label={isResubmission ? "Resubmit Project" : "Submit Project"}
              accessibilityHint="Press to submit your project documents for review"
            />
          )}
          {hasSubmitted && (
            <CustomButton
              text="Project Completed ✅"
              onPress={onBackToRequirements}
              size="m"
              style={styles.completedButton}
              bold={true}
              disabled={loading}
              aria-label="Project Completed - Return to Dashboard"
            />
          )}
          <CustomButton
            text="Back to Requirements"
            onPress={onBackToRequirements}
            styleType="primaryLight"
            size="m"
            style={styles.backButton}
            bold={true}
            disabled={loading}
            aria-label="Go Back to Requirements"
          />
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  content: {
    padding: 20,
  },
  infoBox: {
    padding: 16,
    backgroundColor: "rgba(91, 224, 104, 0.1)",
    borderRadius: 10,
    marginBottom: 24,
    flexDirection: "row",
    alignItems: "flex-start",
    borderWidth: 1,
    borderColor: "rgba(91, 224, 104, 0.3)",
  },
  infoBoxText: {
    color: "#000000",
    lineHeight: 20,
  },
  projectInfo: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  projectTitle: {
    color: "#101828",
    marginBottom: 8,
  },
  projectDescription: {
    color: "#667085",
    lineHeight: 22,
  },
  uploadSection: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  uploadHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  uploadIcon: {
    marginRight: 8,
    color: "#667085",
  },
  uploadTitle: {
    color: "#101828",
  },
  uploadDescription: {
    color: "#667085",
    marginBottom: 16,
    lineHeight: 20,
  },
  previousSubmissions: {
    marginBottom: 24,
  },
  sectionTitle: {
    color: "#101828",
    marginBottom: 12,
  },
  submissionCard: {
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  submissionHeader: {
    marginBottom: 12,
  },
  feedbackSection: {
    marginTop: 12,
  },
  feedbackText: {
    color: "#667085",
    marginTop: 4,
    fontStyle: "italic",
  },
  buttonContainer: {
    flexDirection: "column",
    gap: 12,
  },
  submitButton: {
    // Remove custom background - let CustomButton handle styling
  },
  submitButtonText: {
    color: "#ffffff",
    fontWeight: "600",
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonTextDisabled: {
    color: "#9CA3AF",
  },
  backButton: {
    borderColor: "#3E68FE",
    borderWidth: 2,
    borderRadius: 10,
  },
  infoIcon: {
    fontSize: 16,
  },
  submittedSection: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#F0FDF4",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#BBF7D0",
  },
  submittedHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  checkIcon: {
    marginRight: 8,
    fontSize: 16,
  },
  submittedTitle: {
    color: "#101828",
  },
  submittedDescription: {
    color: "#667085",
    marginBottom: 16,
    lineHeight: 20,
  },
  filesList: {
    marginTop: 8,
  },
  fileItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  fileIcon: {
    marginRight: 12,
    fontSize: 16,
  },
  fileName: {
    flex: 1,
    color: "#374151",
  },
  completedBadge: {
    backgroundColor: "#10B981",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  completedText: {
    color: "#ffffff",
    fontSize: 10,
    fontWeight: "600",
  },
  completedButton: {
    backgroundColor: "#10B981",
    color: "#fff",
  },
});

export default SubmitProject;
